import { useState, useEffect } from 'react';
import { useApi } from '@/hooks/useApi';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Receipt, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

interface TenantOption {
  id: string;
  name: string;
}

interface GenerateTenantSettlementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

/**
 * Carrier/admin modal: pick a tenant, then generate a draft tenant settlement.
 * The carrier-scoped backend (`POST /dashboard/settlements/generate`) sweeps
 * every paid + driver-settled shipment for that tenant not yet in a settlement.
 */
export function GenerateTenantSettlementModal({
  open,
  onOpenChange,
  onSuccess,
}: GenerateTenantSettlementModalProps) {
  const { get } = useApi();
  const { post, loading } = useApi({ showSuccessToast: false, showErrorToast: false });
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [loadingTenants, setLoadingTenants] = useState(false);

  useEffect(() => {
    if (open) {
      fetchTenants();
      setSelectedTenantId('');
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchTenants = async () => {
    setLoadingTenants(true);
    try {
      const result: any = await get('/api/v1/tenants/dropdown');
      const data = result?.success !== undefined ? result?.data : result;
      const list = Array.isArray(data) ? data : (data?.data ?? data?.list ?? []);
      setTenants(
        list.map((t: any) => ({
          id: t.id,
          name:
            t.name_translations?.ar ||
            t.name_translations?.en ||
            t.name ||
            t.subdomain ||
            t.id,
        }))
      );
    } catch {
      toast.error('فشل في تحميل قائمة الشركاء');
    } finally {
      setLoadingTenants(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedTenantId) {
      setError('يرجى اختيار شريك');
      return;
    }

    try {
      await post('/api/v1/dashboard/settlements/generate', {
        tenant_id: selectedTenantId,
      });
      toast.success('تم إنشاء التسوية بنجاح');
      onSuccess();
      onOpenChange(false);
    } catch (err: any) {
      const msg = err?.message ?? 'فشل في إنشاء التسوية';
      setError(msg);
      toast.error(msg);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]" dir="rtl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Receipt className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle>إنشاء تسوية شريك</DialogTitle>
              <DialogDescription className="text-xs">
                سيتم جمع جميع الشحنات المسلّمة والمسوّاة مع السائق غير المضمّنة في تسوية بعد
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label htmlFor="tenant">
              الشريك <span className="text-destructive">*</span>
            </Label>
            {loadingTenants ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                جاري تحميل الشركاء...
              </div>
            ) : (
              <Select
                value={selectedTenantId}
                onValueChange={setSelectedTenantId}
                disabled={loading}
              >
                <SelectTrigger id="tenant">
                  <SelectValue placeholder="اختر شريكاً..." />
                </SelectTrigger>
                <SelectContent>
                  {tenants.length === 0 ? (
                    <SelectItem value="_empty" disabled>
                      لا يوجد شركاء
                    </SelectItem>
                  ) : (
                    tenants.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter className="gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              إلغاء
            </Button>
            <Button type="submit" disabled={loading || !selectedTenantId} className="gap-2">
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  جاري الإنشاء...
                </>
              ) : (
                <>
                  <Receipt className="h-4 w-4" />
                  إنشاء التسوية
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
